
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, FileText, Download, Save } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/components/ui/use-toast";
import { LancamentoExtrato, ContaBancaria } from "@/types/financeiro";

interface ExtratosBancariosProps {
  onImportarExtrato: () => void;
}

const ExtratosBancarios: React.FC<ExtratosBancariosProps> = ({ onImportarExtrato }) => {
  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [lancamentos, setLancamentos] = useState<LancamentoExtrato[]>([]);
  const [contaSelecionada, setContaSelecionada] = useState<string>("");
  const [periodo, setPeriodo] = useState<string>("30dias");
  const [filteredLancamentos, setFilteredLancamentos] = useState<LancamentoExtrato[]>([]);
  const { toast } = useToast();

  const periodosDisponiveis = [
    { value: "hoje", label: "Hoje" },
    { value: "7dias", label: "Últimos 7 dias" },
    { value: "30dias", label: "Últimos 30 dias" },
    { value: "este-mes", label: "Este mês" },
    { value: "mes-anterior", label: "Mês anterior" },
    { value: "personalizado", label: "Período personalizado" },
  ];

  // Load accounts and transactions from localStorage
  useEffect(() => {
    const savedContas = localStorage.getItem('contas_bancarias');
    if (savedContas) {
      setContas(JSON.parse(savedContas));
    }

    const savedLancamentos = localStorage.getItem('lancamentos_extrato');
    if (savedLancamentos) {
      const parsedLancamentos = JSON.parse(savedLancamentos);
      // Convert date strings to Date objects
      const lancamentosWithDates = parsedLancamentos.map((l: any) => ({
        ...l,
        data: new Date(l.data)
      }));
      setLancamentos(lancamentosWithDates);
    }
  }, []);

  // Filter transactions by account and period
  useEffect(() => {
    if (!contaSelecionada) {
      setFilteredLancamentos([]);
      return;
    }

    let filtered = lancamentos.filter(l => l.contaBancariaId === contaSelecionada);
    
    // Apply period filter
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    switch (periodo) {
      case 'hoje':
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        filtered = filtered.filter(l => l.data >= startOfDay && l.data <= today);
        break;
      case '7dias':
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 7);
        sevenDaysAgo.setHours(0, 0, 0, 0);
        filtered = filtered.filter(l => l.data >= sevenDaysAgo && l.data <= today);
        break;
      case '30dias':
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);
        filtered = filtered.filter(l => l.data >= thirtyDaysAgo && l.data <= today);
        break;
      case 'este-mes':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        filtered = filtered.filter(l => l.data >= startOfMonth && l.data <= today);
        break;
      case 'mes-anterior':
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        endOfLastMonth.setHours(23, 59, 59, 999);
        filtered = filtered.filter(l => l.data >= startOfLastMonth && l.data <= endOfLastMonth);
        break;
      default:
        // No additional filtering for 'personalizado' or other values
        break;
    }
    
    // Sort by date
    filtered.sort((a, b) => b.data.getTime() - a.data.getTime());
    
    setFilteredLancamentos(filtered);
  }, [contaSelecionada, periodo, lancamentos]);

  const handleSalvar = () => {
    // In a real app, we would save to a backend
    localStorage.setItem('lancamentos_extrato', JSON.stringify(lancamentos));
    
    toast({
      title: "Extrato salvo",
      description: "Os dados do extrato foram salvos com sucesso",
      duration: 3000,
    });
  };

  const handleExportar = () => {
    if (filteredLancamentos.length === 0) return;
    
    // In a real app, this would generate a CSV or PDF
    toast({
      title: "Exportando extrato",
      description: "O extrato está sendo exportado",
      duration: 3000,
    });
    
    // Simulate download after a brief delay
    setTimeout(() => {
      toast({
        title: "Extrato exportado",
        description: "O arquivo foi baixado com sucesso",
        duration: 3000,
      });
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full md:w-auto">
          <div className="w-full md:w-64">
            <Label htmlFor="conta-select" className="mb-1 block text-xs">Conta</Label>
            <Select value={contaSelecionada} onValueChange={setContaSelecionada}>
              <SelectTrigger id="conta-select" className="text-xs">
                <SelectValue placeholder="Selecione uma conta" />
              </SelectTrigger>
              <SelectContent>
                {contas.length > 0 ? (
                  contas.map((conta) => (
                    <SelectItem key={conta.id} value={conta.id} className="text-xs">
                      {conta.descricao} - {conta.banco}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="sem-contas" disabled className="text-xs">
                    Nenhuma conta cadastrada
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-full md:w-64">
            <Label htmlFor="periodo-select" className="mb-1 block text-xs">Período</Label>
            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger id="periodo-select" className="text-xs">
                <SelectValue placeholder="Selecione um período" />
              </SelectTrigger>
              <SelectContent>
                {periodosDisponiveis.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="text-xs">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          <Button 
            onClick={handleSalvar}
            className="text-white text-xs"
          >
            <Save className="h-4 w-4 mr-1" /> Salvar
          </Button>
          <Button 
            onClick={onImportarExtrato}
            className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark text-xs"
          >
            <Upload className="h-4 w-4 mr-1" /> Importar Extrato
          </Button>
          <Button 
            variant="outline"
            className="border-financeiro-border-DEFAULT text-financeiro-text-DEFAULT text-xs"
            disabled={!contaSelecionada || filteredLancamentos.length === 0}
            onClick={handleExportar}
          >
            <Download className="h-4 w-4 mr-1" /> Exportar
          </Button>
        </div>
      </div>

      {contas.length === 0 ? (
        <Card className="flex items-center justify-center p-10 border-2 border-dashed border-financeiro-border-light rounded-lg text-financeiro-text-light">
          <div className="text-center">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="mb-2 text-xs">Nenhuma conta bancária cadastrada</p>
            <p className="text-xs mb-4">Adicione contas bancárias para visualizar extratos</p>
            <Button 
              onClick={onImportarExtrato}
              className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark text-xs"
            >
              <Upload className="h-4 w-4 mr-1" /> Importar Extrato
            </Button>
          </div>
        </Card>
      ) : contaSelecionada && filteredLancamentos.length === 0 ? (
        <Card className="flex items-center justify-center p-10 border-2 border-dashed border-financeiro-border-light rounded-lg text-financeiro-text-light">
          <div className="text-center">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="mb-2 text-xs">Nenhum lançamento encontrado</p>
            <p className="text-xs mb-4">Não há lançamentos para o período selecionado</p>
            <Button 
              onClick={onImportarExtrato}
              className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark text-xs"
            >
              <Upload className="h-4 w-4 mr-1" /> Importar Extrato
            </Button>
          </div>
        </Card>
      ) : filteredLancamentos.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Data</TableHead>
              <TableHead className="text-xs">Descrição</TableHead>
              <TableHead className="text-xs">Categoria</TableHead>
              <TableHead className="text-right text-xs">Valor</TableHead>
              <TableHead className="text-right text-xs">Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLancamentos.map((lancamento) => (
              <TableRow key={lancamento.id}>
                <TableCell className="text-xs">
                  {format(new Date(lancamento.data), "dd/MM/yyyy", { locale: ptBR })}
                </TableCell>
                <TableCell className="text-xs">{lancamento.descricao}</TableCell>
                <TableCell className="text-xs">{lancamento.categoria}</TableCell>
                <TableCell className={`text-right text-xs ${
                  lancamento.tipo === 'credito' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {lancamento.tipo === 'credito' ? '+' : '-'}
                  {lancamento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
                <TableCell className="text-right text-xs">
                  {lancamento.saldoApos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
};

export default ExtratosBancarios;
